import { useMemo, useState } from 'react';
import {
  useDeleteMtoApplicationMutation,
  useListMtoApplicationsQuery,
  useListMtoLotsQuery,
} from '@shared/api';
import type { LotApplication, MaterialLot } from '@entities/mto';
import { apiErrorMessage, fmtDate } from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Card } from '@shared/ui/Card';
import { Input, Label } from '@shared/ui/Input';
import { IconTrash } from '@shared/ui/icons';
import { AddApplicationForm } from '../components/AddApplicationForm';
import { lotLabel, siteOrDash } from '../helpers';
import {
  Empty,
  ErrBox,
  FilterBar,
  FilterField,
  IconButton,
  Muted,
  SectionTitle,
  Table,
  TableScroll,
  Wrap,
} from '../styles';

export function ApplicationsTab({ canWrite }: { canWrite: boolean }) {
  const [site, setSite] = useState('');
  const [error, setError] = useState<string | null>(null);
  const siteFilter = site.trim();

  const { data, isLoading } = useListMtoApplicationsQuery(
    siteFilter ? { site_id: siteFilter } : undefined,
  );
  const lotsQ = useListMtoLotsQuery();
  const [deleteApp] = useDeleteMtoApplicationMutation();

  const apps = useMemo<LotApplication[]>(() => data?.items ?? [], [data]);
  const lots = useMemo<MaterialLot[]>(() => lotsQ.data?.items ?? [], [lotsQ.data]);
  const lotById = useMemo(() => new Map(lots.map((l) => [l.id, l])), [lots]);

  async function handleDelete(id: string) {
    if (!window.confirm('Удалить факт применения? Действие нельзя отменить.')) return;
    setError(null);
    try {
      await deleteApp(id).unwrap();
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось удалить применение.');
      // eslint-disable-next-line no-console
      console.error('deleteMtoApplication failed', err);
    }
  }

  return (
    <Wrap>
      <FilterBar>
        <FilterField>
          <Label htmlFor="qc-apps-site">Фильтр по объекту</Label>
          <Input
            id="qc-apps-site"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            placeholder="Объект, например ДГП-2 (пусто — все)"
          />
        </FilterField>
      </FilterBar>

      <Card>
        <SectionTitle>
          Применение партий <Badge $tone="neutral">{apps.length}</Badge>
        </SectionTitle>
        {isLoading ? (
          <Muted>Загрузка применений…</Muted>
        ) : apps.length === 0 ? (
          <Empty>
            {siteFilter ? 'По выбранному объекту применений нет.' : 'Фактов применения партий пока нет.'}
          </Empty>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th scope="col">Партия</th>
                  <th scope="col">Объект</th>
                  <th scope="col">Элемент</th>
                  <th scope="col">Ссылка на факт</th>
                  <th scope="col">Дата применения</th>
                  <th scope="col" aria-label="Действия" />
                </tr>
              </thead>
              <tbody>
                {apps.map((app) => {
                  const lot = lotById.get(app.lot_id);
                  return (
                    <tr key={app.id}>
                      <td>{lot ? lotLabel(lot) : app.lot_id}</td>
                      <td>{siteOrDash(app.site_id)}</td>
                      <td>{app.element_id ?? '—'}</td>
                      <td>{app.applied_fact_ref ?? '—'}</td>
                      <td>{fmtDate(app.applied_date)}</td>
                      <td>
                        <IconButton
                          type="button"
                          onClick={() => handleDelete(app.id)}
                          disabled={!canWrite}
                          aria-label="Удалить применение"
                          title={!canWrite ? 'Только просмотр' : 'Удалить применение'}
                        >
                          <IconTrash size={15} />
                        </IconButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        )}
        {error && <ErrBox>{error}</ErrBox>}
      </Card>

      {canWrite && <AddApplicationForm lots={lots} canWrite={canWrite} onError={(m) => setError(m || null)} />}
    </Wrap>
  );
}
